import axios from 'axios';

import { useAppStore } from '@/modules/shared/store/appStore';

import { AUTH_ENDPOINTS, CLIENT_ID, CLIENT_SECRET } from './constants';
import { getAccessToken } from './hooks/useTokenManager';
import type {
  SystemSettings,
  AffiliateProfileResponse,
  LoginParams,
  LoginResponse,
  OpenUserDetails,
  OtpPinResetParams,
  RequestOtpParams,
  ResetPasswordParams,
  UserProfile,
} from './types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Matches legacy `is_email_otp` param logic:
 *   - email login → string "true"  (legacy: `emailLogin ? 'true' : isEmailOtp()`)
 *   - phone login → boolean from systemSettings.is_sms_vendor_down
 */
export function getIsEmailOtp(isEmailLogin: boolean): string | boolean {
  if (isEmailLogin) return 'true';
  const ss = useAppStore.getState().systemSettings as SystemSettings;
  return !!ss?.is_sms_vendor_down;
}

function recaptchaHeaders(token: string | null) {
  if (!token) return undefined;
  return { headers: { 'G-Recaptcha-Response': token } };
}

/** Safely extract the API error code from an unknown error */
export function getApiErrorCode(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: string } } }).response;
    return response?.data?.error;
  }
  return undefined;
}

/** Check if an API error indicates the account is blocked */
export function isBlockedError(err: unknown): boolean {
  if (err && typeof err === 'object' && 'response' in err) {
    const data = (err as { response?: { data?: { message?: string; detail?: string } } }).response?.data;
    return data?.message?.includes?.('blocked') === true || data?.detail?.includes?.('blocked') === true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Account check
// ---------------------------------------------------------------------------

type OpenUserParams =
  | { phone: string; username_type: 'phone' }
  | { phone: string; email: string; username_type: 'email'; is_email_login: true };

export async function getOpenUserDetails(params: OpenUserParams): Promise<OpenUserDetails> {
  const { data } = await axios.post(AUTH_ENDPOINTS.openUserDetails, params);
  return data;
}

// ---------------------------------------------------------------------------
// OTP
// ---------------------------------------------------------------------------

export async function requestOtp(params: RequestOtpParams, recaptchaToken: string | null = null): Promise<void> {
  await axios.post(AUTH_ENDPOINTS.requestOtp, params, recaptchaHeaders(recaptchaToken) ?? undefined);
}

// ---------------------------------------------------------------------------
// Login (PIN or OTP)
// ---------------------------------------------------------------------------

export async function login(
  params: Omit<LoginParams, 'client_id' | 'client_secret' | 'grant_type'>,
  recaptchaToken: string | null = null,
): Promise<LoginResponse> {
  const { data } = await axios.post(
    AUTH_ENDPOINTS.login,
    {
      ...params,
      client_id: CLIENT_ID,
      client_secret: CLIENT_SECRET,
      grant_type: 'password' as const,
    },
    recaptchaHeaders(recaptchaToken) ?? undefined,
  );
  return data;
}

// ---------------------------------------------------------------------------
// PIN reset via OTP
// ---------------------------------------------------------------------------

export async function otpPinReset(params: OtpPinResetParams): Promise<LoginResponse> {
  const { data } = await axios.post(AUTH_ENDPOINTS.otpPinReset, params);
  return data;
}

// ---------------------------------------------------------------------------
// User profile
// ---------------------------------------------------------------------------

export async function getUserProfile(): Promise<UserProfile> {
  const { data } = await axios.get(AUTH_ENDPOINTS.userProfile);
  return data;
}

export async function patchUserProfile(params: Record<string, unknown>): Promise<UserProfile> {
  const { data } = await axios.patch(AUTH_ENDPOINTS.userProfile, params);
  return data;
}

// ---------------------------------------------------------------------------
// Password reset
// ---------------------------------------------------------------------------

export async function resetPassword(params: ResetPasswordParams): Promise<LoginResponse> {
  const { data } = await axios.post(AUTH_ENDPOINTS.resetPassword, params);
  return data;
}

/**
 * Admin-triggered: sends a "set password" email to an affiliate.
 * The recipient clicks the link and lands on /auth/reset-password?type=set-password&uid=X&token=Y
 */
export async function sendSetPasswordEmail(affiliateId: string | number): Promise<void> {
  await axios.post(`${AUTH_ENDPOINTS.setPasswordEmail}${affiliateId}`);
}

// ---------------------------------------------------------------------------
// Token login (SSO from agent portals)
// ---------------------------------------------------------------------------

export async function tokenLogin(t: string): Promise<LoginResponse> {
  const { data } = await axios.post(AUTH_ENDPOINTS.tokenLogin, { t });
  return data;
}

// ---------------------------------------------------------------------------
// Logout
// ---------------------------------------------------------------------------

export async function logout(): Promise<void> {
  await axios.post(AUTH_ENDPOINTS.logout, {
    client_id: CLIENT_ID,
    client_secret: CLIENT_SECRET,
    token: getAccessToken(),
  });
}

// ---------------------------------------------------------------------------
// Affiliate profile
// ---------------------------------------------------------------------------

// Bootstrap-only profile fetch: no commission rates (expensive on the API, unused on landing).
// Flows that need rates fetch them from /crm/affiliates/{id}?with_commission_rates=true.
//
// Keeps this branch's `extraParams`: `Router.tsx` passes `with_affiliate_discounts`, which the
// legacy client used to inject for every caller. main dropped the argument entirely because it
// had no such caller — adopting that verbatim would have silently changed the request.
export async function getAffiliateProfile(extraParams?: Record<string, unknown>): Promise<AffiliateProfileResponse> {
  const { data } = await axios.get(AUTH_ENDPOINTS.affiliateProfile, {
    params: {
      with_instalment_credits: true,
      instalment_credits_with_vendor: true,
      ...extraParams,
    },
  });
  return data;
}

/**
 * One affiliate by id — the `?affiliate=<id>` boot path, where an admin is impersonating.
 *
 * `with_commission_rates` and `with_affiliate_discounts` are defaults rather than the caller's
 * business: the legacy client injected both unless the caller named them, and the boot path never
 * did. Passing `params` overrides either.
 */
export async function getAffiliateById(
  id: string | number,
  params: Record<string, unknown> = {},
): Promise<AffiliateProfileResponse> {
  const { data } = await axios.get(`${AUTH_ENDPOINTS.affiliates}/${id}`, {
    params: { with_commission_rates: true, with_affiliate_discounts: true, ...params },
  });
  return data;
}

/**
 * App-wide settings read once at boot (and refreshed on a long timer) into the app store.
 */
export async function getSystemSettings(): Promise<SystemSettings> {
  const { data } = await axios.get(AUTH_ENDPOINTS.systemSettings);
  return data;
}
